// Rule labels come from constants/broadcastText.js (t.ruleTypes, t.rules.*).
export const RULE_TYPES = ["ALL", "ROLE", "CLIENT", "WORKING_COMPANY", "DEPARTMENT", "ADMIN_LEVEL", "GROUP", "USER"];

let seq = 0;
export const newRule = (type = "ALL") => ({ _key: `r${Date.now()}_${seq++}`, type, exclude: false });

/** Server rule rows (with nested labels) → editable rules. */
export const fromServerRules = (rules = []) =>
  rules.map((r) => ({
    _key: `s${r.id ?? seq++}_${seq++}`,
    type: r.type,
    exclude: !!r.exclude,
    role: r.role ?? undefined,
    clientName: r.clientName ?? undefined,
    workingCompany: r.workingCompany ?? undefined,
    departmentId: r.departmentId ?? undefined,
    adminLevelId: r.adminLevelId ?? undefined,
    groupId: r.groupId ?? undefined,
    userId: r.userId ?? undefined,
    _userLabel: r.user ? r.user.employee?.sr || r.user.email || `User #${r.user.id}` : undefined,
  }));

/** Editable rule → API payload (drops UI-only keys and empty values). */
export const toPayload = (rules = []) =>
  rules.map((r) => {
    const out = { type: r.type, exclude: !!r.exclude };
    const keep = {
      ROLE: ["role"],
      CLIENT: ["clientName"],
      WORKING_COMPANY: ["clientName", "workingCompany"],
      DEPARTMENT: ["departmentId"],
      ADMIN_LEVEL: ["adminLevelId"],
      GROUP: ["groupId"],
      USER: ["userId"],
    }[r.type] || [];
    keep.forEach((k) => {
      out[k] = r[k];
    });
    return out;
  });

export const isRuleComplete = (r) => {
  switch (r.type) {
    case "ALL":
      return true;
    case "ROLE":
      return !!r.role;
    case "CLIENT":
      return !!r.clientName;
    case "WORKING_COMPANY":
      return !!r.clientName && !!r.workingCompany;
    case "DEPARTMENT":
      return !!r.departmentId;
    case "ADMIN_LEVEL":
      return !!r.adminLevelId;
    case "GROUP":
      return !!r.groupId;
    case "USER":
      return !!r.userId;
    default:
      return false;
  }
};

export const rulesReady = (rules) =>
  rules.length > 0 && rules.every(isRuleComplete) && rules.some((r) => !r.exclude);

/** Human summary of one rule in the page language. `options` (target-options) is optional. */
export const ruleLabel = (r, options, t) => {
  const L = t.rules;
  const dept = r.department || options?.departments?.find((d) => d.id === Number(r.departmentId));
  const level = r.adminLevel || options?.adminLevels?.find((l) => l.id === Number(r.adminLevelId));
  const group = r.group || options?.groups?.find((g) => g.id === r.groupId);
  const isAr = t.lang === "ar";
  let label;
  switch (r.type) {
    case "ALL":
      label = t.ruleTypes.ALL;
      break;
    case "ROLE":
      label = t.roles[r.role] || r.role;
      break;
    case "CLIENT":
      label = L.clientRule(r.clientName);
      break;
    case "WORKING_COMPANY":
      label = `${r.clientName} › ${r.workingCompany}`;
      break;
    case "DEPARTMENT":
      label = dept ? L.deptRule((isAr && dept.nameAr) || dept.name, dept.workingCompany) : L.deptN(r.departmentId);
      break;
    case "ADMIN_LEVEL":
      label = level ? L.adminLevelRule(level.name) : L.adminLevelN(r.adminLevelId);
      break;
    case "GROUP":
      label = group ? L.groupRule(group.groupName) : L.group;
      break;
    case "USER":
      label = r.user
        ? (isAr && r.user.employee?.arabicName) || r.user.employee?.sr || r.user.email || L.user(r.userId)
        : r._userLabel || L.user(r.userId);
      break;
    default:
      label = r.type;
  }
  return r.exclude ? L.except(label) : label;
};

export const rulesSummary = (rules, options, t) => {
  if (!rules?.length) return "—";
  const inc = rules.filter((r) => !r.exclude).map((r) => ruleLabel(r, options, t));
  const exc = rules.filter((r) => r.exclude).map((r) => ruleLabel(r, options, t));
  return [inc.join(" + "), exc.join(t.rules.listSep)].filter(Boolean).join(t.rules.listSep);
};
